import React from 'react';
import { Text, TextVariant } from '@naf/text';
import { Table, TableBody, TableCell, TableRow } from '@naf/table';
import { TableOrientation, TableRowType, TableSerializerProps } from './Table.types';
import { tableCellRole } from './tableCellRoles';
import { withWordBreakHints } from './Table.utils';
import * as S from './Table.styles';

interface RowsProps {
  rows: TableRowType[];
  orientation: TableOrientation;
}

// Renders even with no rows: @naf/table's vertical variant styles the body element itself.
const ExpandedRows = ({ rows, orientation }: { rows?: TableRowType[]; orientation: TableOrientation }) => (
  <TableBody>
    {rows?.map((row, rowIndex) => (
      <TableRow key={row._key}>
        {row.cells.map((cell, columnIndex) => {
          const { role, align } = tableCellRole(orientation, rowIndex, columnIndex);
          return (
            // eslint-disable-next-line react/no-array-index-key
            <TableCell key={`${row._key}-${columnIndex}`} align={align} as={role}>
              <S.CellText>{withWordBreakHints(cell)}</S.CellText>
            </TableCell>
          );
        })}
      </TableRow>
    ))}
  </TableBody>
);

// What an accordion holds differs per orientation: three renderings, not one parameterised.
const CollapsedRows = ({ rows, orientation, shouldCollapse }: RowsProps & { shouldCollapse: boolean }) => {
  if (orientation === 'left') {
    return (
      <>
        {rows.map((row) => (
          <S.CollapsedTable
            key={`left-table-collapsed-key-${row._key}`}
            headingLevel={3}
            $shouldCollapse={shouldCollapse}
            title={row.cells[0]}
          >
            {row.cells.slice(1).map((cell, cellIndex) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={`left-table-collapsed-key-${row._key}-${cellIndex}`}>{cell}</div>
            ))}
          </S.CollapsedTable>
        ))}
      </>
    );
  }

  if (orientation === 'crossTable') {
    const columnHeaders = rows[0]?.cells.slice(1) ?? [];
    return (
      <>
        {rows.slice(1).map((row) => (
          <S.CollapsedTable
            key={`cross-table-collapsed-${row._key}`}
            headingLevel={3}
            $shouldCollapse={shouldCollapse}
            title={row.cells[0]}
          >
            {row.cells.slice(1).map((cell, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <Table key={`cross-table-collapsed-${row._key}-${index}`}>
                <TableRow>
                  <TableCell as="th" align="left">
                    <S.CellText>{withWordBreakHints(columnHeaders[index])}</S.CellText>
                  </TableCell>
                  <TableCell align="right">
                    <S.CellText>{withWordBreakHints(cell)}</S.CellText>
                  </TableCell>
                </TableRow>
              </Table>
            ))}
          </S.CollapsedTable>
        ))}
      </>
    );
  }

  if (orientation === 'top') {
    return (
      <>
        {(rows[0]?.cells ?? []).map((columnHeader, columnIndex) => (
          <S.CollapsedTable
            key={`top-table-collapsed-${columnHeader}`}
            headingLevel={3}
            $shouldCollapse={shouldCollapse}
            title={columnHeader}
          >
            {rows.slice(1).map((row) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={`top-table-collapsed-${columnIndex}-${row._key}`}>{row.cells[columnIndex]}</div>
            ))}
          </S.CollapsedTable>
        ))}
      </>
    );
  }

  return null;
};

export const StandardTableView = ({
  value: { title, table, orientation, shouldCollapse, caption },
}: TableSerializerProps) => {
  const rows = table?.rows;

  // Force a minimum column width so columns get room to breathe instead of squeezing text; the
  // table's Outer wrapper (overflow-x: auto) then kicks in to make the rest scrollable on mobile.
  const columnCount = rows?.length ? Math.max(...rows.map((row) => row.cells.length)) : 0;
  const tableMinWidth = columnCount > 0 ? `${Math.max(340, columnCount * 140)}px` : undefined;

  return (
    <>
      <Text variant={TextVariant.Header2}>{title}</Text>
      <S.StyledFigure>
        <S.ExpandedTable shouldCollapse={shouldCollapse} variant="vertical" minWidth={tableMinWidth}>
          <ExpandedRows rows={rows} orientation={orientation} />
        </S.ExpandedTable>
        {rows && <CollapsedRows rows={rows} orientation={orientation} shouldCollapse={shouldCollapse} />}
        {caption && <S.Caption>{caption}</S.Caption>}
      </S.StyledFigure>
    </>
  );
};
